import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type { Case } from '../lib/database';

// URL case identifier is the case_ref slug (e.g. "stiiizy_1"). `case_ref` is
// UNIQUE in the DB (see rds/migrations/001_initial_schema.sql) so the slug
// alone is a stable, shareable, human-readable identifier — much nicer than
// the UUID. CaseShell looks cases up by case_ref; a stale/renamed slug still
// matches the :caseKey route (so NotFoundScreen never fires) and CaseShell
// renders its own case-not-found panel with a Back to Cases recovery.

export interface ParsedCaseKey {
  raw: string;
  slug: string;
}

export function parseCaseKey(raw: string | undefined | null): ParsedCaseKey | null {
  if (!raw) return null;
  // Accept any path-safe slug shape — we don't constrain beyond the router's
  // path-param rules so case_refs like "stiiizy_1" (underscore) keep working.
  return { raw, slug: raw };
}

export function buildCaseKey(c: Pick<Case, 'case_ref'>): string {
  return c.case_ref;
}

// Hook form: reads :caseKey from the route and returns the parsed key.
export function useCaseKey(): ParsedCaseKey | null {
  const { caseKey } = useParams<{ caseKey: string }>();
  return useMemo(() => parseCaseKey(caseKey), [caseKey]);
}
